/**
 * Banker Portal — Outsource case detail + Submit Approval form.
 *
 * V4.24.68 — Aligned with the Type C banker offer form: Rate %, Loan
 * Amount, Tenure, Lock-in, Valid Until, Notes + Upload LO PDF. The
 * outsource banker can now submit the full offer package in one place.
 *
 * Loan Type B: an outsource partner has fanned this case to me. I review
 * the customer/property data and submit my approved offer.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import bankerApi from '../../lib/bankerApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Handshake, Loader2, CheckCircle2, FileUp } from 'lucide-react';

const STATUS_LABEL = {
  waiting_agent_pick: 'Under review',
  waiting_review: 'Under review',
  under_review: 'Under review',
  waiting_banker: 'Awaiting review',
  approved: 'Approved',
  withdrawn: 'Withdrawn',
};

export default function BankerOutsourceCaseDetail() {
  const navigate = useNavigate();
  const { assignment_id } = useParams();
  const [busy, setBusy] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    rate_percent: '',
    loan_amount: '',
    tenure_years: 30,
    lock_in_years: 0,
    valid_until: '',
    notes: '',
  });

  const load = async () => {
    setBusy(true);
    try {
      const r = await bankerApi.get(`/banker/outsource-cases/${assignment_id}`);
      setData(r.data);
      setForm(f => ({
        rate_percent: r.data.rate_percent ?? r.data.approval_rate_percent ?? f.rate_percent,
        loan_amount: r.data.loan_amount ?? r.data.approval_amount ?? r.data.loan_amount_requested ?? f.loan_amount,
        tenure_years: r.data.tenure_years ?? f.tenure_years ?? 30,
        lock_in_years: r.data.lock_in_years ?? f.lock_in_years ?? 0,
        valid_until: r.data.valid_until ?? f.valid_until ?? '',
        notes: r.data.notes ?? f.notes ?? '',
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load case');
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [assignment_id]);

  const submit = async () => {
    if (!form.rate_percent || Number(form.rate_percent) <= 0) {
      toast.error('Enter a rate'); return;
    }
    if (!form.loan_amount || Number(form.loan_amount) <= 0) {
      toast.error('Enter a loan amount'); return;
    }
    if (!form.tenure_years || Number(form.tenure_years) <= 0) {
      toast.error('Enter a tenure'); return;
    }
    setSubmitting(true);
    try {
      const r = await bankerApi.post(`/banker/outsource-cases/${assignment_id}/submit-offer`, {
        rate_percent: parseFloat(form.rate_percent),
        loan_amount: parseFloat(form.loan_amount),
        tenure_years: parseInt(form.tenure_years),
        lock_in_years: parseInt(form.lock_in_years) || 0,
        valid_until: form.valid_until || null,
        notes: form.notes || null,
      });
      toast.success(`Offer approved — case now ${r.data.case_status || 'offer_accepted'}`);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Submit failed'));
    } finally { setSubmitting(false); }
  };

  const uploadLo = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('PDF only'); return; }
    setUploadBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await bankerApi.post(`/banker/outsource-cases/${assignment_id}/upload-lo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Letter of Offer uploaded');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploadBusy(false); e.target.value = ''; }
  };

  if (busy) {
    return <div className="p-10 text-center text-gray-400" data-testid="banker-outsource-loading">
      <Loader2 className="w-6 h-6 animate-spin inline" />
    </div>;
  }
  if (!data) {
    return <div className="p-10 text-center text-gray-400">Case not found or not assigned to you.</div>;
  }

  const approved = data.status === 'approved';

  return (
    <div className="space-y-4" data-testid="banker-outsource-detail">
      <div className="flex items-center justify-between">
        <Link to="/banker/inbox" className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to inbox
        </Link>
        <Badge className={approved ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}
               data-testid="banker-outsource-status-badge">
          {STATUS_LABEL[data.status] || data.status}
        </Badge>
      </div>

      {/* Case header */}
      <div className="bg-white rounded-2xl border p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
            <Handshake className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-gray-900" data-testid="banker-outsource-case-header">
              Case {data.case_ref || data.case_id?.slice(0, 8)} · <span className="text-gray-700">{data.customer_name || '—'}</span>
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Referred by <span className="text-amber-700 font-medium">{data.outsource_partner_name || '—'}</span>
              {data.property_name ? ` · ${data.property_name}` : ''}
              {data.unit_ref ? ` #${data.unit_ref}` : ''}
              {data.loan_amount_requested ? ` · Loan requested RM ${Number(data.loan_amount_requested).toLocaleString('en-MY')}` : ''}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4 text-sm">
          <div><span className="text-gray-500 text-xs">Customer IC</span><br /><span className="font-medium">{data.customer_ic || '—'}</span></div>
          <div><span className="text-gray-500 text-xs">Phone</span><br /><span className="font-medium">{data.customer_phone || '—'}</span></div>
          <div><span className="text-gray-500 text-xs">Email</span><br /><span className="font-medium">{data.customer_email || '—'}</span></div>
          <div><span className="text-gray-500 text-xs">Bank</span><br /><span className="font-medium">{data.bank || '—'}</span></div>
          <div><span className="text-gray-500 text-xs">Branch</span><br /><span className="font-medium">{data.branch || '—'}</span></div>
          <div><span className="text-gray-500 text-xs">Team Leader</span><br /><span className="font-medium">{data.team_leader || '—'}</span></div>
        </div>
      </div>

      {/* Submit form */}
      <div className="bg-white rounded-2xl border p-5 space-y-4" data-testid="banker-outsource-offer-form">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
          {approved && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
          {approved ? 'Approved submission' : 'Your Offer'}
        </h3>
        <p className="text-xs text-gray-500">
          {approved
            ? 'You have already submitted this case. You may still upload / replace the Letter of Offer PDF below.'
            : 'Enter the offer terms you\'re willing to extend. The outsource partner and the customer will see your submission immediately.'}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label>Rate % <span className="text-red-500">*</span></Label>
            <Input
              type="number" step="0.01" min="0"
              value={form.rate_percent}
              onChange={e => setForm({ ...form, rate_percent: e.target.value })}
              disabled={approved}
              data-testid="banker-outsource-rate"
            />
          </div>
          <div>
            <Label>Loan Amount (RM) <span className="text-red-500">*</span></Label>
            <Input
              type="number" min="0"
              value={form.loan_amount}
              onChange={e => setForm({ ...form, loan_amount: e.target.value })}
              disabled={approved}
              data-testid="banker-outsource-amount"
            />
          </div>
          <div>
            <Label>Tenure (years) <span className="text-red-500">*</span></Label>
            <Input
              type="number" min="1" max="50"
              value={form.tenure_years}
              onChange={e => setForm({ ...form, tenure_years: e.target.value })}
              disabled={approved}
              data-testid="banker-outsource-tenure"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Lock-in (years)</Label>
            <Input
              type="number" min="0" max="10"
              value={form.lock_in_years}
              onChange={e => setForm({ ...form, lock_in_years: e.target.value })}
              disabled={approved}
              data-testid="banker-outsource-lockin"
            />
          </div>
          <div>
            <Label>Valid Until</Label>
            <Input
              type="date"
              value={form.valid_until}
              onChange={e => setForm({ ...form, valid_until: e.target.value })}
              disabled={approved}
              data-testid="banker-outsource-valid-until"
            />
          </div>
        </div>

        <div>
          <Label>Notes</Label>
          <textarea
            className="w-full border rounded-md p-2 text-sm min-h-[80px] disabled:bg-gray-50"
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            disabled={approved}
            data-testid="banker-outsource-notes"
          />
        </div>

        {/* Letter of Offer PDF */}
        <div className="border-t pt-4">
          <Label className="font-semibold text-gray-800">Letter of Offer (PDF)</Label>
          {data.lo_pdf_url ? (
            <p className="text-sm text-emerald-700 mt-1">
              ✓ LO uploaded ·{' '}
              <a href={data.lo_pdf_url} target="_blank" rel="noreferrer" className="underline"
                 data-testid="banker-outsource-lo-link">
                View
              </a>
            </p>
          ) : (
            <p className="text-xs text-gray-500 mt-1">No LO uploaded yet.</p>
          )}
          <div className="mt-2">
            <label className="inline-flex">
              <input type="file" accept="application/pdf" hidden onChange={uploadLo}
                     data-testid="banker-outsource-lo-input" />
              <Button variant="outline" asChild disabled={uploadBusy} data-testid="banker-outsource-lo-upload-btn">
                <span>
                  {uploadBusy
                    ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Uploading…</>
                    : <><FileUp className="w-4 h-4 mr-1" /> Upload LO PDF</>}
                </span>
              </Button>
            </label>
          </div>
        </div>

        {approved && (
          <div className="p-3 rounded-md bg-emerald-50 text-emerald-800 text-sm" data-testid="banker-outsource-approved-summary">
            Approved · RM {Number(data.approval_amount || data.loan_amount || 0).toLocaleString('en-MY')}
            {' '}@ {data.approval_rate_percent || data.rate_percent}%
            {data.tenure_years ? ` · ${data.tenure_years}yr` : ''}
            {data.lock_in_years ? ` · lock-in ${data.lock_in_years}yr` : ''}
            {data.fee_on_approval ? ` · Outsource fee RM ${Number(data.fee_on_approval).toLocaleString('en-MY')}` : ''}
          </div>
        )}

        {!approved && (
          <Button onClick={submit} disabled={submitting} className="w-full"
                  data-testid="banker-outsource-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit approval'}
          </Button>
        )}
      </div>
    </div>
  );
}
